import * as Api from '@ViewModels';
import {
	ActionItemViewModel,
	EntityViewModel,
	IEntity,
	IRichContentEntityReference,
	RichContentReferenceMethod,
	getTypeForEntity,
} from '@ViewModels';
import moment from 'moment';
import { ResourceVisibility } from '../../VisibilityDropdown';

export interface IActionItemEditorValues {
	editorState: Api.IRichContentEditorState;
	dueDate?: Date | null;
	/** Omit to keep whatever the item already has; the legacy composer has no time control. */
	hasDueTime?: boolean;
	/** Omit to keep whatever the item already has; `null` clears it. */
	priority?: Api.ActionItemPriority | null;
	assignee?: Api.IUser | null;
	checked?: boolean;
	visibility?: ResourceVisibility;
	mentionedEntities?: EntityViewModel<IEntity>[];
}

/**
 * Build the `IActionItem` payload to persist from the current editor form values. This is the
 * single source of truth for the save shape (raw rich content, due/assignee/completed/visibility,
 * and the `referencedEntities` company/contact split) shared by every action-item editor surface —
 * the legacy modal composer and the flyout editor — so they can't drift.
 */
export function buildActionItemToSave(vm: ActionItemViewModel, values: IActionItemEditorValues): Api.IActionItem {
	const {
		editorState,
		dueDate,
		hasDueTime,
		priority,
		assignee,
		checked = false,
		visibility,
		mentionedEntities,
	} = values;

	const actionItemToSave: Api.IActionItem = { ...vm.toActionItem(true) };
	actionItemToSave.content = editorState.getRawRichTextContent();
	actionItemToSave.dueDate = dueDate ? moment(dueDate).toISOString() : null;
	actionItemToSave.hasDueTime = !!dueDate && (hasDueTime ?? !!actionItemToSave.hasDueTime);
	if (priority !== undefined) {
		actionItemToSave.priority = priority ?? 'None';
	}
	actionItemToSave.assignee = assignee && assignee.id ? { id: assignee.id } : null;
	actionItemToSave.isCompleted = checked;
	actionItemToSave.visibility = visibility;

	actionItemToSave.referencedEntities = {
		companies: [],
		contacts: [],
		users: [],
	};
	(mentionedEntities || []).forEach(entity => {
		const entityModel = entity.toMention();
		const referencedEntityCollection: IRichContentEntityReference<IEntity>[] =
			getTypeForEntity(entityModel) === 'company'
				? actionItemToSave.referencedEntities.companies
				: actionItemToSave.referencedEntities.contacts;
		if (referencedEntityCollection.findIndex(existing => existing.entity.id === entityModel.id) < 0) {
			referencedEntityCollection.push({
				entity: entityModel,
				method: RichContentReferenceMethod.Explicit,
			});
		}
	});

	return actionItemToSave;
}
